import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { ProductContext } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import updateProductInfo from "@insite/client-framework/Store/Components/ProductInfoList/Handlers/UpdateProductInfo";
import { getProductInfoFromList } from "@insite/client-framework/Store/Components/ProductInfoList/ProductInfoListSelectors";
import translate from "@insite/client-framework/Translate";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";

import ProductAddToListLink, {
    ProductAddToListLinkStyles,
} from "@insite/content-library/Components/ProductAddToListLink";
import ProductBrand, { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import ProductDescription, { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import ProductImage, { ProductImageStyles } from "@insite/content-library/Components/ProductImage";
import ProductPartNumbers, { ProductPartNumbersStyles } from "@insite/content-library/Components/ProductPartNumbers";
import ProductPrice from "@insite/content-library/Components/ProductPrice";
import ProductQuantityBreakPricing, {
    ProductQuantityBreakPricingStyles,
} from "@insite/content-library/Components/ProductQuantityBreakPricing";
import ProductUnitOfMeasureSelect from "@insite/content-library/Components/ProductUnitOfMeasureSelect";
import Button, { ButtonIcon, ButtonPresentationProps, ButtonProps } from "@insite/mobius/Button";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import { SelectPresentationProps } from "@insite/mobius/Select";
import { TextFieldProps } from "@insite/mobius/TextField";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import EllipsisVerticalIcon from "@tCentro/Icons/EllipsisVerticalIcon";
import { COLORS, TYPOGRAPHY } from "@tCentro/theme/design";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Minus from "@insite/mobius/Icons/Minus";
import Plus from "@insite/mobius/Icons/Plus";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import getProp from "@insite/mobius/utilities/getProp";
import ProductAlertMessage from "@tCentro/Components/CENProductAlertMessage";
import CENProductDescription, { cenProductDescriptionStyles } from "@tCentro/Components/CENProductDescription";
import ProductSpecialMessage from "@tCentro/Components/CENProductSpecialMessage";
import CENErrorIcon from "@tCentro/Icons/CENErrorIcon";
import ProductAddToCartButton, { InfoIconTooltipStyles } from "@tCentro/Overrides/Components/ProductAddToCartButton";
import ProductQuantityOrdered from "@tCentro/Overrides/Components/ProductQuantityOrdered";
import { getDataLayerUtility } from "@tCentro/Custom/Utilities/getDataLayerUtility";

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => ({
    variantSelectionCompleted: state.pages.productDetails.variantSelectionCompleted,
    productInfo: getProductInfoFromList(state, ownProps.carouselId, ownProps.product.id),
});

const mapDispatchToProps = {
    updateProductInfo,
};

interface OwnProps {
    carouselId: string;
    product: ProductModel;
    showImage: boolean;
    showBrand: boolean;
    showTitle: boolean;
    showPartNumbers: boolean;
    showPrice: boolean;
    showAddToCart: boolean;
    showAddToList: boolean;
    showProductDescription: boolean;
    itemListOverride?: { itemListId?: string; itemListName?: string };
    extendedStyles?: ProductCarouselProductStyles;
}

type Props = OwnProps & ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps>;

export interface ProductCarouselProductStyles {
    tooltipContainerStyles?: InjectableCss;
    errorMsgStyle?: InjectableCss;
    errorIconStyles?: IconPresentationProps;
    errorMsgStyleTypo?: TypographyPresentationProps;
    productImageStyles?: ProductImageStyles;
    productBrandWrapper?: InjectableCss;
    productBrandStyles?: ProductBrandStyles;
    productDescriptionStyles?: ProductDescriptionStyles;
    productPartNumbersStyles?: ProductPartNumbersStyles;
    productPriceWrapper?: InjectableCss;
    productUnitOfMeasureSelectStyles?: SelectPresentationProps;
    productQuantityBreakPricingStyles?: ProductQuantityBreakPricingStyles;
    addToCartContainer?: GridContainerProps;
    qtyGridItem?: GridItemProps;
    productQuantityOrderedStyles?: TextFieldProps;
    addToCartButtonGridItem?: GridItemProps;
    additionalButtonGridItem?: GridItemProps;
    productAddToCartButtonStyles?: ButtonPresentationProps & InfoIconTooltipStyles;
    productAddToListLinkStyles?: ProductAddToListLinkStyles;
    productInfoWrapper?: InjectableCss;
    ellipsisVerticalIcon?: InjectableCss;
    ellipsisVerticalIconWrapper?: InjectableCss;
    productMessageAndSaveWrapper?: InjectableCss;
    productPrice?: InjectableCss;
    defaultProductImage?: InjectableCss;
    subQTYBtn?: ButtonProps;
    addQTYBtn?: ButtonProps;
}

export const productCarouselProductStyles: ProductCarouselProductStyles = {
    errorIconStyles: {
        size: 20,
        css: css`
            span {
                width: 20px;
                height: 20px;
            }
        `,
    },
    errorMsgStyle: {
        css: css`
            display: flex;
            justify-content: center;
            gap: 4px;
            position: absolute;
            margin-top: 4px;
            bottom: -36px;
            @media (min-width: 414px) and (max-width: 540px) {
                bottom: -22px;
            }
        `,
    },
    errorMsgStyleTypo: {
        color: "danger",
        css: css`
            font-weight: 400;
            font-size: 14px;
            line-height: 20px;
        `,
    },
    tooltipContainerStyles: {
        css: css`
            background-color: #262525;
            border-radius: 4px;
            width: max-content;
            border-radius: 4px;
            box-shadow: 0px 4px 8px 0px rgba(38, 37, 37, 0.24);
            position: absolute;
            top: -29px;
            left: -1px;
            z-index: 999;
            font-size: 12px;
            font-style: normal;
            font-weight: 600;
            line-height: 16px;
            padding: 8px 16px;
            color: #262525;
            @media (max-width: 1024px) {
                top: -26px;
                right: 23px;
            }
            &:after {
                content: "";
                border-left: 0px solid ${getProp("theme.typography.p.color")};
                border-right: 4px solid ${getProp("theme.typography.p.color")};
                position: absolute;
                z-index: 999;
                top: 29px;
                left: 42%;
                width: 0px;
                height: 16px;
                width: 0px;
                height: 0px;
                border-left: 10px solid transparent;
                border-right: 10px solid transparent;
                border-top: 16px solid ${getProp("theme.typography.p.color")};
            }
        `,
    },
    addQTYBtn: {
        css: css`
            height: 16px;
            width: 16px;
            background-color: transparent;
            padding: 0px;
            position: absolute;
            left: 113px;
            margin-top: 2px;
            z-index: 1;
            border: none;
            min-height: 16px !important;
            & svg {
                color: ${COLORS.black};
                height: 16px !important;
                width: 16px !important;
                margin-left: 5px;
            }
            &:hover {
                background-color: transparent;
                border: none;
            }
            &:focus {
                outline: none;
            }
            &:disabled,
            &:disabled:hover {
                background: transparent;
            }
            &:active {
                background-color: transparent;
            }
        `,
    },
    subQTYBtn: {
        css: css`
            height: 16px;
            width: 16px;
            background-color: transparent;
            padding: 0px;
            position: absolute;
            left: 56px;
            z-index: 1;
            border: none;
            margin-top: 1px;
            min-height: 16px !important;
            & svg {
                color: ${COLORS.black};
                height: 16px !important;
                width: 16px !important;
            }
            &:hover {
                background-color: transparent;
                border: none;
            }
            &:focus {
                outline: none;
            }
            &:disabled {
                background-color: transparent;
            }
            &:disabled:hover {
                background-color: transparent;
            }
        `,
    },
    productPrice: {
        css: css`
            color: black;
            width: auto;
            font-weight: 600;
            font-size: 14px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        font-size: 12px;
                    `,
                    css`
                        font-size: 12px;
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    productBrandWrapper: {
        css: css`
            margin-bottom: 6px;
            min-height: 22px;
        `,
    },
    productDescriptionStyles: {
        productDetailLink: {
            color: COLORS.black,
            typographyProps: {
                size: 12,
                css: css`
                    margin-bottom: 6px;
                    color: ${getProp("theme.typography.p.color")};
                    font-size: 18px !important;
                    line-height: 24px;
                    font-weight: 600;
                `,
            },
        },
    },
    productPartNumbersStyles: {
        container: {
            css: css`
                flex-grow: 0;
                margin-bottom: 6px;
            `,
        },
        erpNumberLabelText: { size: 13 },
        erpNumberValueText: { size: 13 },
    },
    productPriceWrapper: {
        css: css`
            display: flex;
            align-items: center;
            flex-grow: 1;
            margin-bottom: 10px;
            span[data-test-selector^="productPrice_unitOfMeasureLabel"] {
                font-weight: 400;
                font-size: ${TYPOGRAPHY.size.large};
                line-height: ${TYPOGRAPHY.lineHeight.base};
            }
        `,
    },
    productUnitOfMeasureSelectStyles: {
        cssOverrides: {
            formField: css`
                margin-top: 0px;
            `,
        },
    },
    addToCartContainer: {
        gap: 12,
        css: css`
            flex-grow: 0;
            margin-bottom: 6px;
            max-width: 100%;
            flex-basis: 100%;
            width: 100%;
            display: flex;
            align-items: flex-end;
            > div[class^="GridOffset"] {
                position: relative;
                display: flex;
                align-items: center;
            }
        `,
    },

    qtyGridItem: {
        width: 3,
        css: css`
            max-width: 50%;
            flex-basis: 50%;
            align-items: baseline;
            label{
            width:30%;
            }
        `,
    },
    productQuantityOrderedStyles: {
        cssOverrides: {
            formField: css`
                margin-top: 0;
                width: 70%;
                input {
                    width: 100% !important;
                    margin-left: 2px;
                    text-align: center;
                    &::-webkit-outer-spin-button,
                    &::-webkit-inner-spin-button {
                        -webkit-appearance: none;
                        margin: 0;
                    }
                }
                @media all and (max-width: 1250px) and (min-width: 1160px) {
                    input {
                        width: 100% !important;
                        text-align: center !important;
                        &::-webkit-outer-spin-button,
                        &::-webkit-inner-spin-button {
                            -webkit-appearance: none !important;
                            margin: 0 !important;
                        }
                    }
                }
                input:focus {
                    border-radius: 40px !important;
                }
            `,
        },
    },
    addToCartButtonGridItem: {
        width: 7,
        css: css`
            max-width: 50%;
            flex-basis: 50%;
        `,
    },
    additionalButtonGridItem: { width: 5 },
    productAddToCartButtonStyles: {
        tooltipButtonContainer: {
            css: css`
                @media (min-width: 992px) and (max-width: 1024px) {
                    flex-direction: column-reverse;
                    display: flex;
                    align-items: center;
                }
            `,
        },
        css: css`
            width: 100%;
            border-radius: 16px;
            padding: 8px 16px;
            span {
                font-size: 12px !important;
            }
            @media all and (max-width: 768px) {
                width: 150px !important;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        span {
                            font-size: 10px;
                        }
                        width: 140px !important;
                    `,
                    css`
                        span {
                            font-size: 10px;
                        }
                    `,
                    css`
                        span {
                            font-size: 10px;
                        }
                        width: 142px !important;
                    `,
                    css`
                        span {
                            font-size: 10px;
                        }
                    `,
                    css``,
                ])}
        `,
        typographyProps: {
            size: 14,
        },
    },
    productAddToListLinkStyles: {
        link: {
            color: COLORS.background.tag,
            typographyProps: {
                size: 12,
                css: css`
                    margin-bottom: 8px;
                    color: ${COLORS.background.tag};
                    font-weight: 600;
                    display: flex;
                `,
            },
        },
    },
    productInfoWrapper: {
        css: css`
            padding: 0 8px;
        `,
    },
    productBrandStyles: {
        nameLink: {
            color: COLORS.black,
            typographyProps: {
                css: css`
                    font-weight: 600;
                    font-size: 14px;
                    line-height: 16px;
                    color: ${getProp("theme.typography.p.color")};
                `,
            },
        },
    },

    productMessageAndSaveWrapper: {
        css: css`
            display: flex;
            width: 100%;
            justify-content: space-between;
            margin-bottom: 8px;
        `,
    },
    defaultProductImage: {
        css: css`
            background: #fff;
            justify-content: center;
            img {
                width: 100%;
                max-height: 250px;
                object-fit: contain;
                height: auto;
                aspect-ratio: 1/1;
            }
        `,
    },
};

const TrendingProductCarouselProduct: React.FC<Props> = ({
    carouselId,
    product,
    showImage,
    showBrand,
    showTitle,
    showPartNumbers,
    showPrice,
    showAddToCart,
    showAddToList,
    showProductDescription,
    updateProductInfo,
    extendedStyles,
    variantSelectionCompleted,
    productInfo,
    itemListOverride,
}) => {
    const [styles] = React.useState(() => mergeToNew(productCarouselProductStyles, extendedStyles));
    const [showTooltip, setShowTooltip] = React.useState(false);
    const [carouseldMinErr, setCarouseldMinErr] = React.useState(false);
    const [carouseldMultiErr, setCarouseldMultiErr] = React.useState(false);

    const [btnQty, setBtnQty] = React.useState(1);

    React.useEffect(() => {
        if (
            product?.detail?.multipleSaleQty &&
            product?.detail?.multipleSaleQty > 0 &&
            product?.detail?.roundingRule === "Multiple Only"
        ) {
            setBtnQty(product.detail.multipleSaleQty);
        } else {
            setBtnQty(productInfo?.qtyOrdered !== undefined ? productInfo.qtyOrdered : 1);
        }
    }, [product]);

    const updateQuantity = (qtyOrdered: number) => {
        updateProductInfo({ id: carouselId, productId: product.id, qtyOrdered });
    };

    const updateUnitOfMeasure = (unitOfMeasure: string) => {
        updateProductInfo({ id: carouselId, productId: product.id, unitOfMeasure });
    };
    const addQtyFn = () => {
        setBtnQty(
            btnQty +
            (product?.detail?.multipleSaleQty &&
                product?.detail?.multipleSaleQty > 0 &&
                product?.detail?.roundingRule === "Multiple Only"
                ? product?.detail?.multipleSaleQty
                : 1),
        );
    };
    const subQtyFn = () => {
        const qtyValue =
            btnQty -
            (product?.detail?.multipleSaleQty &&
                product?.detail?.multipleSaleQty > 0 &&
                product?.detail?.roundingRule === "Multiple Only"
                ? product?.detail?.multipleSaleQty
                : 1);
        if (qtyValue === 0 || Math.sign(qtyValue) === -1) {
            setBtnQty(0);
            return false;
        }
        setBtnQty(qtyValue);
    };
    const handleMouseEnter = () => {
        if (
            product?.detail?.multipleSaleQty &&
            product?.detail?.multipleSaleQty > 0 &&
            product?.detail?.roundingRule === "Multiple Only" &&
            btnQty === product?.detail?.multipleSaleQty
        ) {
            setShowTooltip(true);
        } else {
            setShowTooltip(false);
        }
    };

    const handleMouseLeave = () => {
        setShowTooltip(false);
    };

    if (!product || !productInfo) {
        return null;
    }

    const productContext = {
        product,
        productInfo,
        onUnitOfMeasureChanged: updateUnitOfMeasure,
        onQtyOrderedChanged: updateQuantity,
    };

    React.useEffect(() => {
        if (
            productContext?.product?.detail?.multipleSaleQty &&
            productContext?.product?.detail?.multipleSaleQty > 0 &&
            productContext?.product?.detail?.roundingRule === "Multiple Only" &&
            productInfo?.qtyOrdered === 1 &&
            btnQty !== 1
        ) {
            if (productContext?.onQtyOrderedChanged) {
                productContext.onQtyOrderedChanged(productContext?.product?.detail?.multipleSaleQty);
            }
        }
    }, [productInfo?.qtyOrdered]);

    const onProductClickHanlder = () => {
        getDataLayerUtility({
            product,
            pricing: productInfo.pricing,
            quantity: productInfo.qtyOrdered || 1,
            itemObject: {
                item_list_id: itemListOverride?.itemListId || "",
                item_list_name: itemListOverride?.itemListName || "",
            },
        });
    }

        return (
            <>
                <ProductContext.Provider value={productContext}>
                    <StyledWrapper {...styles.productMessageAndSaveWrapper} data-test-selector="specialMessagesSaveWrapper">
                        <StyledWrapper>
                            {product.properties.specialMessage && (
                                <ProductSpecialMessage specialMessage={product.properties.specialMessage} />
                            )}
                            {product.properties.alertMessage && (
                                <ProductAlertMessage alertMessage={product.properties.alertMessage} />
                            )}
                        </StyledWrapper>
                        <StyledWrapper>
                            {showAddToList && (
                                <ProductAddToListLink
                                    data-test-selector="addToListLink"
                                    extendedStyles={styles.productAddToListLinkStyles}
                                />
                            )}
                        </StyledWrapper>
                    </StyledWrapper>

                    <StyledWrapper {...styles.productInfoWrapper}>
                        {showImage && product.smallImagePath && product.mediumImagePath ? (
                            <ProductImage onClick={onProductClickHanlder} product={productContext} extendedStyles={styles.productImageStyles} />
                        ) : (
                            <Clickable
                                href={productContext.productInfo.productDetailPath}
                                data-test-selector="productImage"
                                className="defaultProductImage"
                                {...styles.defaultProductImage}
                            >
                                <LazyImage
                                    src="/userFiles/images/default-product-image.png"
                                    altText="Default product image"
                                />
                            </Clickable>
                        )}

                        <StyledWrapper>
                            {showBrand && product.brand && (
                                <StyledWrapper {...styles.productBrandWrapper} data-test-selector="productBrandWrapper">
                                    <ProductBrand brand={product.brand} extendedStyles={styles.productBrandStyles} />
                                </StyledWrapper>
                            )}
                            {showTitle && (
                                <ProductDescription
                                    product={productContext}
                                    onClick={onProductClickHanlder}
                                    extendedStyles={styles.productDescriptionStyles}
                                />
                            )}
                            {showPartNumbers && (
                                <ProductPartNumbers
                                    data-test-selector="productMPNNumber"
                                    productNumber={product.productNumber}
                                    customerProductNumber={product.customerProductNumber}
                                    manufacturerItem={product.manufacturerItem}
                                    showCustomerName={false}
                                    showManufacturerItem={true}
                                    extendedStyles={styles.productPartNumbersStyles}
                                />
                            )}
                            {showProductDescription && (
                                <StyledWrapper>
                                    <CENProductDescription product={productContext} />
                                </StyledWrapper>
                            )}

                            {showPrice && (
                                <StyledWrapper {...styles.productPriceWrapper}>
                                    <VisuallyHidden as="label" htmlFor={`${carouselId}-uom`} id={`${carouselId}-uom-label`}>
                                        {translate("U/M")}
                                    </VisuallyHidden>
                                    <ProductPrice showLabel={false} product={productContext} />
                                    {product.unitOfMeasures && (
                                        <ProductUnitOfMeasureSelect
                                            labelOverride=""
                                            extendedStyles={styles.productUnitOfMeasureSelectStyles}
                                            id={`${carouselId}-uom`}
                                        />
                                    )}
                                    <ProductQuantityBreakPricing
                                        extendedStyles={styles.productQuantityBreakPricingStyles}
                                    />
                                </StyledWrapper>
                            )}
                        </StyledWrapper>
                    </StyledWrapper>
                    {showAddToCart && (
                        <GridContainer {...styles.addToCartContainer}>
                            <GridItem {...styles.qtyGridItem}>
                                <Typography
                                    as="label"
                                    {...styles.productPrice}
                                    htmlFor={`${carouselId}-qty`}
                                    id={`${carouselId}-qty-label`}
                                >
                                    {translate("QTY :")}
                                </Typography>
                                <Button
                                    disabled={
                                        !!(
                                            (!variantSelectionCompleted && product?.isVariantParent) ||
                                            (product?.detail?.multipleSaleQty &&
                                                product?.detail?.multipleSaleQty > 0 &&
                                                product?.detail?.roundingRule === "Multiple Only" &&
                                                btnQty === product?.detail?.multipleSaleQty)
                                        )
                                    }
                                    {...styles.subQTYBtn}
                                    data-test-selector="subQTYBtn"
                                    onClick={subQtyFn}
                                    onMouseOver={handleMouseEnter}
                                    onMouseLeave={handleMouseLeave}
                                >
                                    <ButtonIcon src={Minus} />
                                </Button>
                                {showTooltip && (
                                    <StyledWrapper {...styles.tooltipContainerStyles}>
                                        <Typography
                                            as="span"
                                            css={css`
                                            color: ${getColor("info.contrast")} !important;
                                        `}
                                        >
                                            {translate(`${product?.detail?.multipleSaleQty} unit minimum`)}
                                        </Typography>
                                    </StyledWrapper>
                                )}
                                <ProductQuantityOrdered
                                    labelOverride=""
                                    extendedStyles={styles.productQuantityOrderedStyles}
                                    id={`${carouselId}-qty`}
                                    onBtnclickQtyValue={btnQty}
                                    setBtnQty={setBtnQty}
                                    setCMinErr={setCarouseldMinErr}
                                    setCMultiErr={setCarouseldMultiErr}
                                    isProductCarousel={true}
                                />
                                <Button
                                    disabled={!variantSelectionCompleted && product?.isVariantParent}
                                    {...styles.addQTYBtn}
                                    data-test-selector="addQTYBtn"
                                    onClick={addQtyFn}
                                >
                                    <ButtonIcon src={Plus} />
                                </Button>
                                {carouseldMultiErr && (
                                    <StyledWrapper {...styles.errorMsgStyle}>
                                        <Icon {...styles.errorIconStyles} src={CENErrorIcon} />
                                        <Typography {...styles.errorMsgStyleTypo}>
                                            {translate(
                                                `This product must be ordered in multiples of ${product?.detail?.multipleSaleQty}`,
                                            )}
                                        </Typography>
                                    </StyledWrapper>
                                )}
                                {carouseldMinErr && (
                                    <StyledWrapper {...styles.errorMsgStyle}>
                                        <Icon {...styles.errorIconStyles} src={CENErrorIcon} />
                                        <Typography {...styles.errorMsgStyleTypo}>
                                            {translate(
                                                `This product has a minimum order of ${product?.detail?.multipleSaleQty} units`,
                                            )}
                                        </Typography>
                                    </StyledWrapper>
                                )}
                            </GridItem>
                            <GridItem {...styles.addToCartButtonGridItem}>
                                <ProductAddToCartButton
                                    data-test-selector="addToCartBtn"
                                    extendedStyles={styles.productAddToCartButtonStyles}
                                    itemListOverride={itemListOverride}
                                />
                            </GridItem>
                        </GridContainer>
                    )}
                </ProductContext.Provider>
            </>
        );
    };

    export default connect(mapStateToProps, mapDispatchToProps)(TrendingProductCarouselProduct);
